import { StationDef, Vec3 } from './types';

// ═══════════════════════════════════════════════════════════
//  Default scene layout — used when the backend snapshot has
//  not arrived yet. Also defines the *visual* station geometry
//  (desk mesh, chair, screen) independent from the authoritative
//  logical station that the backend emits.
// ═══════════════════════════════════════════════════════════

export interface StationVisual {
  id: string;
  position: Vec3;
  facing: number;
  label: string;
  color: string;
  type: 'desk' | 'review-board' | 'build-station' | 'diagram-wall';
}

export const DEFAULT_STATIONS: Record<string, StationVisual> = {
  'desk-coder': {
    id: 'desk-coder',
    position: { x: -3, y: 0, z: 0 },
    facing: 0.2,
    label: 'Code Station',
    color: '#8b5cf6',
    type: 'desk',
  },
  'desk-orchestrator': {
    id: 'desk-orchestrator',
    position: { x: 0, y: 0, z: -1 },
    facing: 0.0,
    label: 'Command Hub',
    color: '#f97316',
    type: 'review-board',
  },
  'desk-tester': {
    id: 'desk-tester',
    position: { x: 3, y: 0, z: 0 },
    facing: -0.2,
    label: 'QA Bay',
    color: '#06b6d4',
    type: 'build-station',
  },
  'desk-architect': {
    id: 'desk-architect',
    position: { x: 0, y: 0, z: 2 },
    facing: 3.14,
    label: 'Architecture Wall',
    color: '#10b981',
    type: 'diagram-wall',
  },
};

export const DEFAULT_STATIONS_AS_LOGICAL: Record<string, StationDef> = Object.fromEntries(
  Object.entries(DEFAULT_STATIONS).map(([k, v]) => [k, { position: v.position, facing: v.facing }])
);

// ─── Camera presets ────────────────────────────────────
export interface CameraPreset {
  position: Vec3;
  target: Vec3;
  fov: number;
}

export const CAMERA_PRESETS: Record<string, CameraPreset> = {
  overview: {
    position: { x: 0, y: 8, z: 12 },
    target:   { x: 0, y: 1, z:  0 },
    fov: 45,
  },
  closeup_coder: {
    position: { x: -3, y: 2, z: 5 },
    target:   { x: -3, y: 1, z: 0 },
    fov: 35,
  },
  closeup_orchestrator: {
    position: { x: 0, y: 2, z: 4 },
    target:   { x: 0, y: 1, z: -1 },
    fov: 35,
  },
  top_down: {
    position: { x: 0, y: 15, z: 0.01 },
    target:   { x: 0, y: 0, z: 0 },
    fov: 50,
  },
};

// ─── Lighting rig ──────────────────────────────────────
export const LIGHTING = {
  ambient: { intensity: 0.35, color: '#8899ff' },
  key: {
    position: { x: 6, y: 8, z: 6 },
    intensity: 1.2,
    color: '#ffffff',
  },
  rim: {
    position: { x: -6, y: 4, z: -6 },
    intensity: 0.8,
    color: '#a78bfa',
  },
  floor: {
    color: '#0b0f1c',
    gridColor: '#7c3aed',
    size: 30,
    divisions: 30,
  },
};
